/**
 * The main-thread side of the index worker: queue, backpressure, progress,
 * cancel (DI-2-1 §2.6).
 *
 * Backpressure: files are posted in chunks of ≤ 64 and never more than 2
 * chunks are outstanding (posted, not yet acked), so a 10 000-file drop does
 * not structured-clone 10 000 `File` objects in one go.
 *
 * Results are buffered and applied to the store in batches (at most one
 * store update per `flushMs`), so the main thread does a few renders per
 * second however fast the worker is — that is what keeps a 1 000-file index
 * free of long tasks (AC-9 (3)).
 *
 * Lanes: `min(8, navigator.hardwareConcurrency ?? 4)` by default, configurable
 * (DI-D13 c1 "eight files in flight, configurable").
 */

import type { StoreApi } from "zustand";
import type { IndexState } from "../indexStore.js";
import type { HashResult, ParsedImage, ValidityIssue } from "./types.js";
import type { IndexJob, WorkerRequest, WorkerResponse } from "./protocol.js";
import { EMBEDDED_MAX_LONG_EDGE, embeddedPreview } from "./preview.js";

export interface WorkerLike {
  postMessage(msg: WorkerRequest): void;
  addEventListener(
    type: "message",
    listener: (e: MessageEvent<WorkerResponse>) => void,
  ): void;
  terminate(): void;
  /** A worker that fails to load or throws reports here (a real Worker has it). */
  onerror?: ((ev: ErrorEvent) => unknown) | null;
}

export const CHUNK_SIZE = 64;
export const MAX_OUTSTANDING_CHUNKS = 2;
export const DEFAULT_FLUSH_MS = 150;

/** One waiting preview: a file at one size. */
function previewKey(index: number, longEdge: number): string {
  return `${String(index)}@${String(longEdge)}`;
}

export function defaultLanes(): number {
  const hc =
    typeof navigator !== "undefined"
      ? navigator.hardwareConcurrency
      : undefined;
  return Math.max(1, Math.min(8, hc ?? 4));
}

export function createParseWorker(): WorkerLike {
  return new Worker(new URL("./parse.worker.ts", import.meta.url), {
    type: "module",
    name: "intake-index",
  });
}

export interface IndexClientOptions {
  store: StoreApi<IndexState>;
  createWorker?: () => WorkerLike;
  lanes?: number;
  chunkSize?: number;
  maxOutstandingChunks?: number;
  /** 0 = apply every message immediately (tests). */
  flushMs?: number;
}

export interface PreviewAnswer {
  blob: Blob | null;
  issue?: ValidityIssue;
}

export class IndexClient {
  private readonly store: StoreApi<IndexState>;
  private readonly createWorker: () => WorkerLike;
  private readonly lanes: number;
  private readonly chunkSize: number;
  private readonly maxOutstanding: number;
  private readonly flushMs: number;

  private worker: WorkerLike | null = null;
  private jobs: IndexJob[] = [];
  private nextFile = 0;
  private nextChunkId = 0;
  private outstanding = new Set<number>();
  private sessionId = "";
  private cancelled = false;
  private endSent = false;

  private pendingParsed: { index: number; result: ParsedImage }[] = [];
  private pendingHashed: { index: number; hashes: HashResult }[] = [];
  private pendingErrors: { index?: number; message: string }[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Waiting preview answers by `<index>@<longEdge>` (one file, two sizes at once). */
  private previews = new Map<string, ((a: PreviewAnswer) => void)[]>();

  /** Chunk sizes as posted, and the most chunks ever outstanding — read by AC-7. */
  readonly stats = { chunkSizes: [] as number[], maxOutstandingSeen: 0 };

  constructor(opts: IndexClientOptions) {
    this.store = opts.store;
    this.createWorker = opts.createWorker ?? createParseWorker;
    this.lanes = opts.lanes ?? defaultLanes();
    this.chunkSize = Math.min(opts.chunkSize ?? CHUNK_SIZE, CHUNK_SIZE);
    this.maxOutstanding = opts.maxOutstandingChunks ?? MAX_OUTSTANDING_CHUNKS;
    this.flushMs = opts.flushMs ?? DEFAULT_FLUSH_MS;
  }

  /**
   * Starts indexing `jobs` (index i of the store's rows = jobs[i]).
   * DI-3-P2 — `knownHashes` (a resume): the persisted hashes by `rowHashKey`;
   * the worker skips the hash read of every job whose key is in it.
   */
  start(
    sessionId: string,
    jobs: readonly IndexJob[],
    opts: { knownHashes?: Record<string, HashResult> } = {},
  ): void {
    this.dispose();
    this.sessionId = sessionId;
    this.jobs = jobs.slice();
    this.nextFile = 0;
    this.nextChunkId = 0;
    this.outstanding.clear();
    this.cancelled = false;
    this.endSent = false;
    this.stats.chunkSizes = [];
    this.stats.maxOutstandingSeen = 0;
    if (jobs.length === 0) return;
    const w = this.createWorker();
    this.worker = w;
    w.addEventListener("message", (e) => {
      if (this.worker === w) this.onMessage(e.data);
    });
    // A module worker that cannot load (or throws outside a job) must not look
    // like an index that is merely slow: name it and stop.
    w.onerror = (ev: ErrorEvent) => {
      if (this.worker !== w) return;
      const message =
        ev instanceof ErrorEvent && ev.message
          ? ev.message
          : "the index worker failed to start";
      this.pendingErrors.push({ message });
      this.flush();
      this.store.getState().setPhase("error");
    };
    w.postMessage(
      opts.knownHashes && Object.keys(opts.knownHashes).length > 0
        ? { type: "config", lanes: this.lanes, knownHashes: opts.knownHashes }
        : { type: "config", lanes: this.lanes },
    );
    this.pump();
  }

  cancel(): void {
    if (this.cancelled) return;
    this.cancelled = true;
    this.worker?.postMessage({ type: "cancel" });
    this.flush();
    this.store.getState().setPhase("cancelled");
  }

  /** Asks the worker for a preview of one file; resolves with null where none can be made. */
  preview(index: number, longEdge = 1600): Promise<PreviewAnswer> {
    const w = this.worker;
    if (!w) return Promise.resolve({ blob: null });
    const file = this.jobs[index]?.file;
    if (file && longEdge <= EMBEDDED_MAX_LONG_EDGE)
      return embeddedPreview(file, longEdge)
        .catch(() => null)
        .then((r) =>
          r && this.worker === w
            ? { blob: r.blob }
            : this.askWorker(w, index, longEdge),
        );
    return this.askWorker(w, index, longEdge);
  }

  private askWorker(
    w: WorkerLike,
    index: number,
    longEdge: number,
  ): Promise<PreviewAnswer> {
    if (this.worker !== w) return Promise.resolve({ blob: null });
    return new Promise((resolve) => {
      const key = previewKey(index, longEdge);
      const waiting = this.previews.get(key) ?? [];
      waiting.push(resolve);
      this.previews.set(key, waiting);
      w.postMessage({ type: "preview", index, longEdge });
    });
  }

  dispose(): void {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.worker?.terminate();
    this.worker = null;
    for (const [, resolvers] of this.previews)
      for (const r of resolvers) r({ blob: null });
    this.previews.clear();
  }

  /** Applies every buffered result to the store now. */
  flush(): void {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    if (
      !this.pendingParsed.length &&
      !this.pendingHashed.length &&
      !this.pendingErrors.length
    )
      return;
    const batch = {
      parsed: this.pendingParsed,
      hashed: this.pendingHashed,
      errors: this.pendingErrors,
    };
    this.pendingParsed = [];
    this.pendingHashed = [];
    this.pendingErrors = [];
    this.store.getState().applyBatch(batch);
  }

  private scheduleFlush(): void {
    if (this.flushMs <= 0) {
      this.flush();
      return;
    }
    this.flushTimer ??= setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, this.flushMs);
  }

  private pump(): void {
    const w = this.worker;
    if (!w || this.cancelled) return;
    while (
      this.outstanding.size < this.maxOutstanding &&
      this.nextFile < this.jobs.length
    ) {
      const files = this.jobs.slice(
        this.nextFile,
        this.nextFile + this.chunkSize,
      );
      const chunkId = this.nextChunkId++;
      this.outstanding.add(chunkId);
      this.stats.chunkSizes.push(files.length);
      this.stats.maxOutstandingSeen = Math.max(
        this.stats.maxOutstandingSeen,
        this.outstanding.size,
      );
      w.postMessage({
        type: "index",
        sessionId: this.sessionId,
        chunkId,
        baseIndex: this.nextFile,
        files,
      });
      this.nextFile += files.length;
    }
    if (this.nextFile >= this.jobs.length && !this.endSent) {
      this.endSent = true;
      w.postMessage({ type: "end" });
    }
  }

  private onMessage(msg: WorkerResponse): void {
    if (this.cancelled && msg.type !== "preview") return;
    switch (msg.type) {
      case "parsed":
        this.pendingParsed.push({ index: msg.index, result: msg.result });
        this.scheduleFlush();
        return;
      case "hashed":
        this.pendingHashed.push({ index: msg.index, hashes: msg.hashes });
        this.scheduleFlush();
        return;
      case "error":
        this.pendingErrors.push(
          msg.index === undefined
            ? { message: msg.message }
            : { index: msg.index, message: msg.message },
        );
        this.scheduleFlush();
        return;
      case "chunk-ack":
        this.outstanding.delete(msg.chunkId);
        this.pump();
        return;
      case "progress":
        return; // the store counts from the rows; progress is informational
      case "preview": {
        const key = previewKey(msg.index, msg.longEdge);
        const waiting = this.previews.get(key) ?? [];
        this.previews.delete(key);
        const answer: PreviewAnswer = msg.issue
          ? { blob: msg.blob, issue: msg.issue }
          : { blob: msg.blob };
        for (const r of waiting) r(answer);
        return;
      }
      case "done":
        this.flush();
        this.store.getState().setPhase("done");
        return;
    }
  }
}
